import { cva } from "class-variance-authority";

/**
 * Button Variants
 *
 * V&A Waterfront Design System - Primary Button
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=9858-7266
 *
 * Color tokens auto-switch via `.dark` class on a parent element.
 *
 * Variants:
 * - blue-filled / white-filled: Filled background button (Primary)
 *   → Uses button-primary-* tokens. On dark backgrounds, wrap in `.dark` parent.
 * - blue-outline / white-outline: Outlined border button (Outline)
 *   → Uses button-outline-* tokens + semantic tokens (brand-primary, text-primary, etc.)
 *
 * Sizes:
 * - lg: 48px height (h-12), 24px horizontal padding, 12px vertical padding
 * - sm: 40px height (h-10), 16px horizontal padding, 8px vertical padding
 */

const filledClasses = [
  "bg-button-primary-background text-button-primary-text [&_svg]:text-button-primary-icon",
  "hover:bg-button-primary-background-hover",
  "focus-visible:bg-button-primary-background focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-0",
  "disabled:bg-button-primary-background-disabled disabled:text-button-primary-text-disabled disabled:[&_svg]:text-button-primary-icon-disabled",
  "data-[disabled]:bg-button-primary-background-disabled data-[disabled]:text-button-primary-text-disabled data-[disabled]:[&_svg]:text-button-primary-icon-disabled",
];

const blurredClasses = [
  "backdrop-blur-lg [&_svg]:text-current",
  "shadow-[inset_0_0_0_1px_var(--button-blurred-border,transparent)]",
  "bg-[color:var(--button-blurred-background,color-mix(in_oklab,var(--color-white)_30%,transparent))]",
  "text-[color:var(--button-blurred-text,var(--color-white))]",
  "hover:bg-[color:var(--button-blurred-background-hover,color-mix(in_oklab,var(--color-white)_40%,transparent))]",
  "focus-visible:bg-[color:var(--button-blurred-background-hover,color-mix(in_oklab,var(--color-white)_40%,transparent))] focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-0",
  "disabled:bg-white/10 disabled:text-white/50 disabled:[&_svg]:text-white/50",
  "data-[disabled]:bg-white/10 data-[disabled]:text-white/50 data-[disabled]:[&_svg]:text-white/50",
];

const outlineClasses = [
  "bg-transparent text-text-primary border border-brand-primary [&_svg]:text-icon-primary",
  "hover:bg-button-outline-background-hover hover:border-brand-primary",
  "focus-visible:bg-transparent focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-0",
  "disabled:bg-button-outline-background-disabled disabled:text-button-outline-text-disabled disabled:border-border-disabled disabled:[&_svg]:text-button-outline-icon-disabled",
  "data-[disabled]:bg-button-outline-background-disabled data-[disabled]:text-button-outline-text-disabled data-[disabled]:border-border-disabled data-[disabled]:[&_svg]:text-button-outline-icon-disabled",
];

export const buttonVariants = cva(
  // Base styles
  [
    "inline-flex flex-row items-center justify-center text-left",
    "rounded-[var(--button-primary-radius,var(--radius-sm))] transition-colors cursor-pointer",
    "disabled:pointer-events-none",
    "data-[disabled]:pointer-events-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
    "focus:outline-none",
  ],
  {
    variants: {
      variant: {
        // Blue Filled - Primary CTA for light backgrounds
        "blue-filled": filledClasses,
        // White Filled - Primary CTA for dark backgrounds (same tokens, switched via .dark)
        "white-filled": filledClasses,
        // Blue Outline - Secondary for light backgrounds
        "blue-outline": outlineClasses,
        // White Outline - Secondary for dark backgrounds (same tokens, switched via .dark)
        "white-outline": outlineClasses,
        // Blurred - frosted glass CTA over photography/video
        blurred: blurredClasses,
      },
      size: {
        // Large - H48 (min-height; grows if label wraps)
        lg: [
          "min-h-12 px-6 py-3 gap-2",
          "typography-component-md-medium",
          "[&_svg]:size-6",
        ],
        // Small - H40 (min-height; grows if label wraps)
        sm: [
          "min-h-10 px-4 py-2 gap-1",
          "typography-component-sm-medium",
          "[&_svg]:size-6",
        ],
      },
    },
    defaultVariants: {
      variant: "blue-filled",
      size: "lg",
    },
  },
);
